import {
  isStagedMatchConfirmable,
  isStagedMatchPriceable,
  isStagedPartConfirmable,
  isStagedPartPriceable,
} from '@misumi/shared'
import type { IdentifiedPart, MatchedPart, StagingBomNode, StagingTriage } from './staging-types'

/**
 * UID-402 (ADR-0124) — the Triage page's PRIMARY-CTA and resolution-gating
 * domain rules, as a pure function over the triage.
 *
 * The rules come from the IMTS Cleanup Bash review and are pinned by the
 * canonical design source's own derivation (`Fictiv Platform.dc.html:9526`):
 *
 * ```js
 * const stgCtaKey = allUnrecognized ? 'match'
 *   : ((isBomSession || nConfig>0 || nQuote>0 || nMatch>0) ? 'rfq' : 'cart');
 * ```
 *
 * Two properties of that derivation are load-bearing and were BOTH violated by
 * the pre-UID-402 implementation:
 *
 * 1. **It is SESSION-WIDE, not selection-scoped.** The design derives the CTA
 *    from `stg` (every staged row), never from the current checkbox selection.
 *    `StagingPage` previously derived it from `selectedMatched`, so the primary
 *    action changed meaning as the user ticked boxes and read "Review parts"
 *    (disabled) for an untouched session that the system could price outright.
 * 2. **"Can we price it?" is the ONLY question.** `cart` (checkout) is reached
 *    exactly when nothing needs a human step; anything else is an RFQ.
 *
 * DELIBERATELY NOT derived from `StagingTriage.analysisSignal`, even though the
 * server's `buildStagingAnalysisSignal` encodes the same predicate: that field
 * is OPTIONAL on the contract (a defensive fallback for pre-UID-255 responses
 * and fixtures). Deriving the CTA from it would make an absent signal silently
 * mean "everything is priceable" — i.e. it would offer checkout for a session
 * full of unconfigured custom parts. So the CTA is derived locally from the
 * triage arrays.
 *
 * Gate-5 W2 — deriving locally does NOT mean re-implementing the rules. The
 * predicates now come from `@misumi/shared`'s `staging-priceability.ts`, which
 * `apps/api`'s `buildStagingAnalysisSignal` imports too. Previously each side
 * kept its own copy "mirrored field-for-field" and comment-enforced to "move
 * together" — they did not, and the resulting gap shipped a real bug (W1). One
 * owner, two importers: the FE can decide WHEN to trust the wire signal while
 * still being incapable of disagreeing with it about WHAT priceable means.
 */

/**
 * Gate-5 W2 — these three are now thin, typed adapters over the SINGLE owner in
 * `@misumi/shared` (`staging-priceability.ts`), which `apps/api`'s
 * `buildStagingAnalysisSignal` and `applyConfirmParts` mirror-by-import rather
 * than by comment. They are kept as named local functions rather than
 * re-exported directly for two reasons: they bind this vertical's own
 * `IdentifiedPart`/`MatchedPart` types (so a call site gets a real type error
 * rather than a structural near-miss), and every existing import in this
 * vertical keeps working unchanged.
 *
 * The rules themselves — and WHY each clause is what it is — are documented on
 * the shared functions. Do not restate them here; that is how the duplication
 * that caused W1 started.
 */
export function isIdentifiedPriceable(part: IdentifiedPart): boolean {
  return isStagedPartPriceable(part)
}

export function isMatchedPriceable(part: MatchedPart): boolean {
  return isStagedMatchPriceable(part)
}

export function isMatchedConfirmable(part: MatchedPart): boolean {
  return isStagedMatchConfirmable(part)
}

/**
 * UID-425 (ADR-0130 D3) — thin typed adapter over `@misumi/shared`'s
 * {@link isStagedPartConfirmable}, the single owner of the IDENTIFIED-part
 * confirm-batch guard (mirrors `applyConfirmParts` clause for clause). Do NOT
 * hand-roll this a second time — that structural mistake is exactly what
 * ADR-0124 Gate-5 W1 shipped as a real bug for the matched pair.
 */
export function isIdentifiedConfirmable(part: IdentifiedPart): boolean {
  return isStagedPartConfirmable(part)
}

/**
 * `blocked` — items still need resolution. RFQ creation is GATED on those
 *   specific items (UID-402 domain rule) — the primary CTA renders but cannot
 *   be actioned until `filesToResolve` is empty. Takes precedence over every
 *   other outcome, including `checkout`: an unresolved orphan drawing may yet
 *   turn into a part, so a session with one is never "fully priced".
 * `rfq` — every item is resolved, but at least one part needs a human step
 *   (configuration, or an ambiguous/optionless match) before it can be priced.
 * `checkout` — the system can price everything; go straight to checkout.
 * `none` — nothing staged at all; there is no next step to offer.
 */
export type StagingCtaKind = 'none' | 'blocked' | 'rfq' | 'checkout'

export interface StagingCta {
  readonly kind: StagingCtaKind
  /** `filesToResolve.length` — the count the gating message names. */
  readonly needsResolutionCount: number
  /** Non-ignored parts the system cannot price without a human step. */
  readonly unpriceableCount: number
  /**
   * Gate-5 W6 — EVERY row on screen, ignored matched parts included.
   *
   * `kind` is derived from ACTIONABLE rows only, so an all-ignored session
   * collapses to `'none'`. Without this field the summary card could not tell
   * that apart from a genuinely empty session, and rendered the same thing for
   * both: no CTA and no explanation, while the ignored rows sat visibly on the
   * page. `stagedRowCount > 0 && kind === 'none'` is precisely "there are rows,
   * but none of them is actionable", which the card explains in words.
   */
  readonly stagedRowCount: number
}

/**
 * Every `kind: 'part'` leaf in the captured hierarchy, at any depth. Assembly
 * nodes are containers and are not rows in this sense.
 */
function collectBomPartLeaves(nodes: readonly StagingBomNode[]): readonly StagingBomNode[] {
  const leaves: StagingBomNode[] = []
  const visit = (node: StagingBomNode) => {
    if (node.kind === 'part') leaves.push(node)
    for (const child of node.children ?? []) visit(child)
  }
  for (const node of nodes) visit(node)
  return leaves
}

export function deriveStagingCta(triage: StagingTriage): StagingCta {
  const needsResolutionCount = triage.filesToResolve.length
  // `ignored` matches are excluded outright (resolved/set-aside), matching the
  // server signal's own `continue` for them — NOT counted as unpriceable.
  const liveMatched = triage.matched.filter((part) => part.status !== 'ignored')

  const unpriceableCount =
    triage.identified.filter((part) => !isIdentifiedPriceable(part)).length +
    liveMatched.filter((part) => !isMatchedPriceable(part)).length

  // ACTIONABLE rows drive `kind`: ignored matches are excluded, so a session
  // whose every match is ignored has nothing to price, quote or check out.
  const actionableRowCount = triage.identified.length + liveMatched.length + needsResolutionCount

  /**
   * AUTHORIZED DEVIATION from ADR-0124 D2 (human ruling, 2026-09-04) —
   * see `docs/adr/0153-bom-session-primary-cta-interim-rfq.md`.
   *
   * ADR-0124 D2 derived `kind` from priceability alone and pointedly did NOT
   * adopt the design's first disjunct (`Fictiv Platform.dc.html:9528`):
   *
   *     stgCtaKey = allUnrecognized ? 'match'
   *       : ((isBomSession || nConfig>0 || nQuote>0 || nMatch>0) ? 'rfq' : 'cart')
   *
   * ADR-0142 then recorded, twice, that the hierarchy "gates nothing" and that
   * "no line of `staging-cta.ts` changes", and its Residual R1 left the
   * consequence open for pm + product-designer.
   *
   * THE CONSEQUENCE, observed: a real MISUMI BOM export arrives with
   * `identified`, `matched` and `filesToResolve` ALL empty — its lines are
   * `bomHierarchy` nodes and do not enter those arrays until they resolve. So
   * `actionableRowCount === 0`, `kind` collapsed to `'none'`, and
   * `StagingSummaryCard` hid the primary CTA outright. The page offered no
   * next step at all: "Review in library" was the only enabled control.
   *
   * R1's own preferred target is the OPPOSITE of this clause — an unresolved
   * BOM line is resolution work, which would route the session to `'blocked'`
   * (CTA rendered, disabled, with a reason) and satisfy D3. That remains the
   * better model, and it is still owed. It is NOT implementable on today's
   * contract: `filesToResolve` is file-shaped, not BOM-line-shaped, so there is
   * nowhere to put a BOM line. This clause is the interim, and the ADR records
   * it as such.
   *
   * WHY IT IS SAFE DESPITE D3. `blocked` still takes precedence below, so a
   * BOM session with unresolved FILES is gated exactly as before. And an
   * enabled "Create RFQ" on a session with nothing confirmable cannot create an
   * empty RFQ: `StagingPage`'s `handleCreateRfq` already refuses to navigate
   * when `confirmableIds.length === 0` and explains why inline. The button
   * appearing is therefore an honest offer of the next step, not a trapdoor.
   *
   * ADR-0142:507 warned against a SECOND `isBomSession` boolean sourced from
   * somewhere else (a file extension, an upload flag) — "how the single gate
   * quietly becomes two disagreeing gates". Heeded: this is derived from the
   * captured hierarchy itself, the same origin the tree renders from, and from
   * nothing else.
   */
  const bomPartLeaves = collectBomPartLeaves(triage.bomHierarchy ?? [])
  const isBomSession = bomPartLeaves.length > 0

  /**
   * BOM leaves whose `partId` does NOT resolve into `identified`/`matched` —
   * i.e. the rows that render from the hierarchy alone and are counted nowhere
   * else. Adding all leaves would double-count a resolved leaf, which is
   * already one of the triage rows below.
   */
  const triagePartIds = new Set<string>([
    ...triage.identified.map((part) => part.partId),
    ...triage.matched.map((part) => part.partId),
  ])
  const unresolvedBomRowCount = bomPartLeaves.filter(
    (leaf) => !leaf.partId || !triagePartIds.has(leaf.partId),
  ).length

  // Gate-5 W6 — EVERY row that renders, ignored included. Deliberately NOT used
  // to pick `kind`: counting ignored rows as actionable would land an
  // all-ignored session on `'checkout'` (its `unpriceableCount` is 0 precisely
  // because there is nothing left to price), sending the user to `/cart` with
  // nothing addable. `'none'` stays the honest answer; this count is what lets
  // the card SAY so instead of falling silent.
  const stagedRowCount =
    triage.identified.length + triage.matched.length + needsResolutionCount + unresolvedBomRowCount

  const kind: StagingCtaKind =
    actionableRowCount === 0 && !isBomSession
      ? 'none'
      : needsResolutionCount > 0
        ? 'blocked'
        : isBomSession || unpriceableCount > 0
          ? 'rfq'
          : 'checkout'

  return { kind, needsResolutionCount, unpriceableCount, stagedRowCount }
}

/** Button copy for each outcome. `blocked` still reads "Create RFQ" — the
 * gating is communicated by the disabled state + an explicit reason caption,
 * not by relabelling the action into something the user didn't ask for. */
export const STAGING_CTA_LABEL: Record<Exclude<StagingCtaKind, 'none'>, string> = {
  blocked: 'Create RFQ',
  rfq: 'Create RFQ',
  checkout: 'Checkout',
}
